import React, { useState, useMemo } from "react";
import { Sidebar } from "../components/Sidebar";
import { OfferHeader } from "../components/offer/OfferHeader";
import { OfferCard } from "../components/offer/OfferCard";
import { AIRecommendationEngine } from "../components/offer/AIRecommendationEngine";
import { MarketInsightsPanel } from "../components/offer/MarketInsightsPanel";
import { offers, aiVerdict } from "../data/offerData";
import { ArrowDownUp, LayoutGrid, Rows3, Filter } from "lucide-react";

export default function OfferIntelligencePage() {
  const [sortBy, setSortBy] = useState("score");
  const [view, setView] = useState("grid");

  const sorted = useMemo(() => {
    const list = [...offers];
    if (sortBy === "score") list.sort((a, b) => b.score - a.score);
    if (sortBy === "price") list.sort((a, b) => b.offerNum - a.offerNum);
    if (sortBy === "speed") list.sort((a, b) => parseInt(a.speed) - parseInt(b.speed));
    if (sortBy === "risk") list.sort((a, b) => a.fallthrough - b.fallthrough);
    return list;
  }, [sortBy]);

  const sortBtns = [
    { id: "score", label: "AI score" },
    { id: "price", label: "Price" },
    { id: "speed", label: "Speed" },
    { id: "risk", label: "Risk" },
  ];

  return (
    <div className="min-h-screen" style={{ background: "var(--cf-app-bg)" }}>
      <Sidebar />
      <main className="md:ml-[var(--cf-sidebar-w,300px)] transition-[margin-left] duration-300 ease-out xl:mr-[360px] flex flex-col min-h-screen" data-testid="offer-main">
        <OfferHeader />

        <div className="px-4 md:px-10 py-8 space-y-8">
          {/* AI engine */}
          <AIRecommendationEngine />

          {/* Comparison toolbar */}
          <div className="flex items-center gap-3 flex-wrap">
            <h2 className="cf-h-section mr-2">Offer comparison</h2>
            <span className="text-[11.5px] text-slate-500">{offers.length} offers received · sorted by</span>
            <div className="flex items-center gap-1 bg-white rounded-lg border border-slate-200 p-1">
              {sortBtns.map((b) => (
                <button
                  key={b.id}
                  data-testid={`sort-${b.id}`}
                  onClick={() => setSortBy(b.id)}
                  className={`h-8 px-3 rounded-md text-[12.5px] font-medium transition-all ${
                    sortBy === b.id ? "bg-slate-900 text-white shadow-sm" : "text-slate-600 hover:text-slate-900"
                  }`}
                >
                  {b.label}
                </button>
              ))}
            </div>
            <div className="ml-auto flex items-center gap-2">
              <button data-testid="offer-filter-btn" className="h-9 px-3 rounded-lg bg-white border border-slate-200 text-slate-600 hover:border-slate-300 inline-flex items-center gap-1.5 text-[12.5px] font-medium">
                <Filter size={13} /> Filter
              </button>
              <div className="flex items-center bg-white border border-slate-200 rounded-lg p-1">
                <button
                  data-testid="offer-grid-btn"
                  onClick={() => setView("grid")}
                  className={`w-8 h-7 rounded-md flex items-center justify-center transition-colors ${view === "grid" ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-900"}`}
                >
                  <LayoutGrid size={13} />
                </button>
                <button
                  data-testid="offer-rows-btn"
                  onClick={() => setView("rows")}
                  className={`w-8 h-7 rounded-md flex items-center justify-center transition-colors ${view === "rows" ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-900"}`}
                >
                  <Rows3 size={13} />
                </button>
              </div>
            </div>
          </div>

          {/* Offer cards */}
          <div className={view === "grid" ? "grid grid-cols-1 lg:grid-cols-2 gap-5" : "space-y-5"}>
            {sorted.map((o) => (
              <OfferCard
                key={o.id}
                offer={o}
                isRecommended={o.id === aiVerdict.recommended}
              />
            ))}
          </div>
        </div>
      </main>
      <MarketInsightsPanel />
    </div>
  );
}
